import React, { createContext, useContext, useCallback, useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null);

  const apply = (data) => setCart(data && !data.empty ? data : null);

  const refresh = useCallback(async () => {
    if (!user || user.role !== "customer") { setCart(null); return; }
    try { const { data } = await api.get("/cart"); apply(data); } catch { setCart(null); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const addItem = async (restaurant, item, replace = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/cart/add", {
        restaurant_id: restaurant.id, menu_item_id: item.menu_item_id,
        variant: item.variant || null, addons: item.addons || [], spice: item.spice || null,
        instructions: item.instructions || "", qty: item.qty || 1, replace,
      });
      apply(data);
      return { ok: true };
    } catch (e) {
      if (e?.response?.status === 409) { setConflict({ restaurant, item }); return { conflict: true }; }
      throw e;
    } finally { setBusy(false); }
  };

  const resolveConflict = async (clearAndAdd) => {
    const c = conflict; setConflict(null);
    if (clearAndAdd && c) await addItem(c.restaurant, c.item, true);
  };

  const updateLine = async (lineId, delta) => {
    try { const { data } = await api.post(`/cart/item/${lineId}`, { delta }); apply(data); }
    catch (e) { toast.error(apiError(e)); await refresh(); }
  };
  const removeLine = async (lineId) => {
    try { const { data } = await api.delete(`/cart/item/${lineId}`); apply(data); }
    catch (e) { toast.error(apiError(e)); await refresh(); }
  };
  const clear = async () => { try { await api.delete("/cart"); } catch { /* ignore */ } setCart(null); };
  const setMeta = async (payload) => { const { data } = await api.post("/cart/meta", payload); apply(data); };

  const value = {
    cart, busy, count: cart?.count || 0, subtotal: cart?.food_subtotal || 0, total: cart?.total || 0,
    addItem, updateLine, removeLine, clear, setMeta, refresh, resolveConflict,
  };

  return (
    <CartCtx.Provider value={value}>
      {children}
      {conflict && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-end sm:items-center justify-center p-4" data-testid="cart-conflict-dialog">
          <div className="bg-white rounded-2xl max-w-[420px] w-full p-5">
            <div className="flex items-center gap-2 text-ink"><AlertTriangle className="text-coral" size={20} /><h3 className="font-display font-bold text-lg">Items from another restaurant</h3></div>
            <p className="text-sm text-ink-600 mt-2">Your cart contains items from another restaurant. Clear it and add this item instead?</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => resolveConflict(false)} className="flex-1 border border-border rounded-xl py-2.5 font-semibold text-sm" data-testid="keep-cart-btn">Keep Current Cart</button>
              <button onClick={() => resolveConflict(true)} className="flex-1 bg-coral text-white rounded-xl py-2.5 font-bold text-sm" data-testid="clear-cart-add-btn">Clear Cart &amp; Add</button>
            </div>
          </div>
        </div>
      )}
    </CartCtx.Provider>
  );
}
